// 


import { useState } from "react";
import {
  FileText,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  Wrench,
  DollarSign,
  Scale,
  Phone,
  Mail,
  Bike,
  CreditCard,
  ClipboardCheck,
  PackageCheck,
} from "lucide-react";
import { motion } from "framer-motion";

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState(null);

  const terms = [
    {
      id: "service-booking",
      icon: <ClipboardCheck className="w-5 h-5" />,
      title: "Service Booking",
      content:
        "Customers may book bike servicing through the OkMechanics website or application. The customer is responsible for providing accurate contact details, vehicle information, service requirements, and preferred service date and time.",
    },
    {
      id: "service-inspection",
      icon: <Bike className="w-5 h-5" />,
      title: "Vehicle Inspection",
      content:
        "The vehicle may be inspected before servicing to identify existing damage, mechanical issues, and additional service requirements. Any additional work identified during inspection may require customer approval before the work is carried out.",
    },
    {
      id: "service-estimate",
      icon: <DollarSign className="w-5 h-5" />,
      title: "Service Estimate and Charges",
      content:
        "The estimated service amount is based on the services and parts selected at the time of booking. The final amount may change if additional repairs or replacement parts are required. Customers will be informed of additional charges where reasonably possible before such work is performed.",
    },
    {
      id: "additional-work",
      icon: <Wrench className="w-5 h-5" />,
      title: "Additional Repairs",
      content:
        "If additional repairs or replacement parts are required beyond the original booking, OkMechanics may contact the customer for approval. Additional work may be performed only after receiving appropriate customer confirmation, except where immediate action is reasonably necessary to protect the vehicle from further damage.",
    },
    {
      id: "parts",
      icon: <PackageCheck className="w-5 h-5" />,
      title: "Parts and Components",
      content:
        "Replacement parts will be selected according to the service requirement and availability. Where applicable, customers may be informed whether a part is original, genuine, OEM-equivalent, or aftermarket. Part availability may affect service completion time.",
    },
    {
      id: "service-time",
      icon: <Clock className="w-5 h-5" />,
      title: "Service Time",
      content:
        "The service date and estimated completion time provided by OkMechanics are indicative and may change due to vehicle condition, additional repairs, parts availability, workshop workload, weather, transportation delays, or other circumstances beyond reasonable control.",
    },
    {
      id: "pickup-drop",
      icon: <MapPin className="w-5 h-5" />,
      title: "Pickup and Drop Service",
      content:
        "Where pickup and drop service is selected, the customer must provide an accurate pickup location and ensure that the vehicle is available at the agreed time. Pickup and drop charges, where applicable, will be displayed or communicated before confirmation.",
    },
    {
      id: "vehicle-condition",
      icon: <CheckCircle className="w-5 h-5" />,
      title: "Vehicle Condition",
      content:
        "The customer should disclose known mechanical problems, previous accident damage, modifications, security devices, and other relevant information before servicing. Existing damage should be identified during the inspection or vehicle handover process where reasonably possible.",
    },
    {
      id: "customer-belongings",
      icon: <Shield className="w-5 h-5" />,
      title: "Personal Belongings",
      content:
        "Customers should remove cash, documents, valuables, accessories, and personal belongings from the vehicle before handing it over for servicing. OkMechanics is not responsible for personal items left inside the vehicle unless required by applicable law.",
    },
    {
      id: "payment",
      icon: <CreditCard className="w-5 h-5" />,
      title: "Payment",
      content:
        "Customers must pay the applicable service charges, parts charges, pickup and drop charges, taxes, and other disclosed fees applicable to the booking. Payment methods available through the platform may include online payment and other methods offered by OkMechanics.",
    },
    {
      id: "cancellation",
      icon: <Clock className="w-5 h-5" />,
      title: "Cancellation",
      content:
        "Customers may cancel a service booking subject to the cancellation terms displayed at the time of booking. Cancellation charges, if applicable, will depend on the booking status, timing of cancellation, services already performed, pickup arrangements, and other applicable charges.",
    },
    {
      id: "refund",
      icon: <DollarSign className="w-5 h-5" />,
      title: "Refunds",
      content:
        "Eligible refunds will be processed according to the applicable refund policy and payment method. Amounts already paid for services, parts, pickup, transportation, or work already completed may not be refundable where the service has already been performed or costs have been incurred.",
    },
    {
      id: "service-quality",
      icon: <Wrench className="w-5 h-5" />,
      title: "Service Quality and Rework",
      content:
        "If a customer believes that a covered service was not completed properly, the customer should contact OkMechanics promptly with the booking details and issue description. OkMechanics may inspect the vehicle and, where appropriate, provide corrective service in accordance with the applicable service or warranty terms.",
    },
    {
      id: "road-test",
      icon: <Bike className="w-5 h-5" />,
      title: "Road Testing",
      content:
        "Certain repairs may require a limited road test by an authorized mechanic or service representative to verify vehicle operation. Such testing will be performed only as reasonably necessary for diagnosis, repair, or quality verification.",
    },
    {
      id: "customer-responsibility",
      icon: <Shield className="w-5 h-5" />,
      title: "Customer Responsibility",
      content:
        "Customers are responsible for providing accurate information, following service instructions, collecting the vehicle after service completion, paying applicable charges, and informing OkMechanics of any issue noticed after service without unreasonable delay.",
    },
    {
      id: "service-delay",
      icon: <AlertCircle className="w-5 h-5" />,
      title: "Service Delays",
      content:
        "Service completion may be delayed because of additional mechanical issues, parts availability, supplier delays, workshop conditions, pickup or delivery issues, or other circumstances beyond reasonable control. OkMechanics will make reasonable efforts to communicate material delays.",
    },
    {
      id: "damage",
      icon: <AlertCircle className="w-5 h-5" />,
      title: "Damage During Service or Transportation",
      content:
        "Vehicle condition may be recorded at the time of pickup or handover. If damage is reported, OkMechanics may review inspection records, photographs, service records, and other available information to determine the circumstances and appropriate resolution.",
    },
    {
      id: "fraudulent-information",
      icon: <AlertCircle className="w-5 h-5" />,
      title: "Incorrect or Fraudulent Information",
      content:
        "Customers must not provide false, misleading, or fraudulent information about vehicle ownership, vehicle condition, payment details, identity, or service requirements. OkMechanics may suspend or cancel bookings where fraudulent activity is reasonably suspected.",
    },
    {
      id: "limitation",
      icon: <Scale className="w-5 h-5" />,
      title: "Limitation of Liability",
      content:
        "OkMechanics will take reasonable care while providing its services. To the extent permitted by applicable law, OkMechanics will not be responsible for indirect, incidental, or consequential losses arising from service delays or circumstances outside its reasonable control.",
    },
    {
      id: "force-majeure",
      icon: <AlertCircle className="w-5 h-5" />,
      title: "Events Beyond Our Control",
      content:
        "OkMechanics will not be responsible for delays or failure to provide services caused by circumstances beyond reasonable control, including natural disasters, severe weather, government restrictions, strikes, transportation disruptions, technical failures, or other similar events.",
    },
    {
      id: "privacy",
      icon: <Shield className="w-5 h-5" />,
      title: "Privacy and Personal Data",
      content:
        "Personal information provided through the platform may be used to process bookings, communicate with customers, provide services, process payments, provide support, and meet legal or regulatory requirements. Personal data will be handled in accordance with the applicable OkMechanics Privacy Policy and applicable law.",
    },
    {
      id: "changes",
      icon: <FileText className="w-5 h-5" />,
      title: "Changes to These Terms",
      content:
        "OkMechanics may update these Terms and Conditions from time to time to reflect changes in services, business practices, technology, or applicable law. The updated version will be published on the website or application with an updated effective date.",
    },
  ];

  const mustDo = [
    "Provide accurate customer and vehicle information",
    "Describe known vehicle problems before service",
    "Inspect the vehicle condition during handover where possible",
    "Remove valuables and personal belongings before handing over the vehicle",
    "Approve additional work or charges when required",
    "Pay all applicable service and parts charges",
    "Collect the vehicle after service completion",
    "Report service-related concerns promptly",
  ];

  const mustNot = [
    "Provide false or misleading vehicle or customer information",
    "Request unauthorized or illegal modifications or repairs",
    "Use the service platform for fraudulent activities",
    "Remove or misuse parts installed during servicing",
    "Refuse legitimate disclosed charges for approved additional work",
    "Leave valuables or important personal belongings inside the vehicle",
    "Attempt to misuse payment, refund, coupon, or promotional systems",
    "Use the platform for unlawful purposes",
  ];

  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.05 },
    },
  };

  const cardItem = {
    hidden: { opacity: 0, y: 16 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">

      {/* Hero */}
      <div className="relative bg-[#111111] border-b border-white/[0.08] py-16 sm:py-20 px-4 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              `linear-gradient(#FF6B35 1px, transparent 1px), ` +
              `linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[120px] opacity-5 pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="w-14 h-14 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <FileText className="w-7 h-7 text-[#FF6B35]" />
          </div>

          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">
            Legal
          </span>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
            Terms & Conditions
          </h1>

          <p className="text-base sm:text-lg text-gray-400">
            Please read these terms carefully before booking a bike service
            with OkMechanics.
          </p>
        </motion.div>
      </div>

      {/* Notice */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-start gap-4 p-5 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />

          <div>
            <h3 className="text-sm font-bold text-white mb-1">
              Agreement Acknowledgment
            </h3>

            <p className="text-sm text-gray-400 leading-relaxed">
              By using the OkMechanics website or application and booking a
              service, you acknowledge that you have read, understood, and
              agreed to these Terms and Conditions, together with the
              applicable Privacy Policy and Refund Policy.
            </p>
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-2xl font-black text-white text-center mb-2"
        >
          Detailed Terms & Conditions
        </motion.h2>

        <p className="text-center text-gray-500 mb-8 text-sm">
          Please review the following terms before booking a bike service.
        </p>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {terms.map((term, index) => (
            <motion.div
              key={term.id}
              id={term.id}
              variants={cardItem}
              className="bg-[#111111] border border-white/[0.08] rounded-2xl p-5 hover:border-[#FF6B35]/20 transition-colors"
            >
              <div className="w-10 h-10 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center mb-3 text-[#FF6B35]">
                {term.icon}
              </div>

              <h3 className="text-sm font-bold text-white mb-2">
                {index + 1}. {term.title}
              </h3>

              <p className="text-xs text-gray-500 leading-relaxed">
                {term.content}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Customer Obligations */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-8">
          Your Obligations as a Customer
        </h2>

        <div className="grid md:grid-cols-2 gap-4">

          <div className="bg-[#111111] border border-green-500/20 rounded-2xl p-6">
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              You Must
            </h3>

            <ul className="space-y-2">
              {mustDo.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-400"
                >
                  <span className="text-green-400 font-bold mt-0.5">
                    •
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#111111] border border-red-500/20 rounded-2xl p-6">
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              You Must Not
            </h3>

            <ul className="space-y-2">
              {mustNot.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-400"
                >
                  <span className="text-red-400 font-bold mt-0.5">
                    •
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>

      {/* Legal Notice */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-[#111111] border border-white/[0.08] rounded-2xl p-6 sm:p-8">

          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-lg font-bold text-white">
              Legal and Service Enforcement
            </h2>
          </div>

          <p className="text-sm text-gray-400 mb-3 leading-relaxed">
            If a customer violates these terms, OkMechanics may take
            reasonable action depending on the circumstances, applicable
            service terms, and applicable law. This may include:
          </p>

          <ul className="space-y-1.5 text-sm text-gray-500">
            {[
              "Cancellation or suspension of a service booking",
              "Recovery of legitimate outstanding service charges",
              "Charges for approved additional work or replacement parts",
              "Investigation of disputed vehicle damage or service issues",
              "Restriction of access in cases of fraud, abuse, or misuse",
              "Taking appropriate legal action where required",
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="text-[#FF6B35]">•</span>
                {item}
              </li>
            ))}
          </ul>

          <p className="text-xs text-gray-600 border-t border-white/[0.06] mt-4 pt-4">
            Nothing in these Terms is intended to exclude or restrict any
            consumer rights or other rights that cannot lawfully be excluded
            or restricted under applicable law.
          </p>
        </div>
      </div>

      {/* Governing Law */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-[#111111] border border-white/[0.08] rounded-2xl p-6 sm:p-8">

          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-5 h-5 text-[#FF6B35]" />

            <h2 className="text-lg font-bold text-white">
              Governing Law and Disputes
            </h2>
          </div>

          <p className="text-sm text-gray-400 leading-relaxed">
            These Terms shall be interpreted in accordance with the applicable
            laws of India. Customers should first contact OkMechanics to
            attempt to resolve any service-related concern or dispute
            amicably. Nothing in these Terms limits any rights or remedies
            available to consumers under applicable law.
          </p>
        </div>
      </div>

      {/* Contact */}
      <div className="max-w-5xl mx-auto px-4 py-8 pb-16">

        <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl p-6 sm:p-8">

          <h2 className="text-lg font-bold text-white text-center mb-6">
            Questions About Our Terms?
          </h2>

          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">

            <a
              href="tel:+919923887090"
              className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors"
            >
              <Phone className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />

              <div>
                <div className="text-xs text-gray-500">
                  Call Us
                </div>

                <div className="font-semibold text-sm">
                  +91 9923887090
                </div>
              </div>
            </a>

            <a
              href="mailto:okmechanics@gmail.com"
              className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors"
            >
              <Mail className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />

              <div>
                <div className="text-xs text-gray-500">
                  Email Us
                </div>

                <div className="font-semibold text-sm break-all">
                  okmechanics@gmail.com
                </div>
              </div>
            </a>

          </div>
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          Last Updated: August 2026 · © 2026 OkMechanics. All rights reserved.
        </p>

      </div>
    </div>
  );
};

export default TermsAndConditions;
